import { ENDPOINTS } from '@/constants/api'
import { QUERY_KEYS } from '@/constants/query-keys'
import type { QueryOf, RequestBodyOf, ResponseData } from '@/types/api'
import { apiSlice } from './base'

export type Asset = ResponseData<'/api/v1/assets/{id}', 'get'>
export type AssetTreeNode = ResponseData<'/api/v1/assets/tree', 'get'>[number]
type AssetsListData = ResponseData<'/api/v1/assets/', 'get'>
type AssetsQuery = QueryOf<'/api/v1/assets/', 'get'>
type CreateAssetBody = RequestBodyOf<'/api/v1/assets/', 'post'>
type UpdateAssetBody = RequestBodyOf<'/api/v1/assets/{id}', 'patch'>

export const assetsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAssets: builder.query<AssetsListData, AssetsQuery | void>({
      query: (params) => ({ url: ENDPOINTS.ASSETS.ROOT, ...(params ? { params } : {}) }),
      transformResponse: (response: { data: AssetsListData }) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.items.map((a) => ({ type: QUERY_KEYS.ASSET, id: a.id }) as const),
              { type: QUERY_KEYS.ASSET, id: 'LIST' } as const,
            ]
          : [{ type: QUERY_KEYS.ASSET, id: 'LIST' } as const],
    }),
    getAssetTree: builder.query<AssetTreeNode[], void>({
      query: () => ENDPOINTS.ASSETS.TREE,
      transformResponse: (response: { data: AssetTreeNode[] }) => response.data,
      providesTags: [{ type: QUERY_KEYS.ASSET, id: 'TREE' }],
    }),
    getAssetById: builder.query<Asset, string>({
      query: (id) => ENDPOINTS.ASSETS.BY_ID(id),
      transformResponse: (response: { data: Asset }) => response.data,
      providesTags: (_r, _e, id) => [{ type: QUERY_KEYS.ASSET, id }],
    }),
    createAsset: builder.mutation<Asset, CreateAssetBody>({
      query: (body) => ({ url: ENDPOINTS.ASSETS.ROOT, method: 'POST', body }),
      transformResponse: (response: { data: Asset }) => response.data,
      invalidatesTags: [
        { type: QUERY_KEYS.ASSET, id: 'LIST' },
        { type: QUERY_KEYS.ASSET, id: 'TREE' },
      ],
    }),
    updateAsset: builder.mutation<Asset, { id: string; body: UpdateAssetBody }>({
      query: ({ id, body }) => ({ url: ENDPOINTS.ASSETS.BY_ID(id), method: 'PATCH', body }),
      transformResponse: (response: { data: Asset }) => response.data,
      invalidatesTags: (_r, _e, { id }) => [
        { type: QUERY_KEYS.ASSET, id },
        { type: QUERY_KEYS.ASSET, id: 'LIST' },
        { type: QUERY_KEYS.ASSET, id: 'TREE' },
      ],
    }),
    deleteAsset: builder.mutation<void, string>({
      query: (id) => ({ url: ENDPOINTS.ASSETS.BY_ID(id), method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: QUERY_KEYS.ASSET, id },
        { type: QUERY_KEYS.ASSET, id: 'LIST' },
        { type: QUERY_KEYS.ASSET, id: 'TREE' },
      ],
    }),
  }),
  overrideExisting: false,
})

export const {
  useGetAssetsQuery,
  useGetAssetTreeQuery,
  useGetAssetByIdQuery,
  useCreateAssetMutation,
  useUpdateAssetMutation,
  useDeleteAssetMutation,
} = assetsApi
